/**
 * Claim-intent persistence (DEV-170). A claim-link recipient may have no
 * account, and every auth path can lose in-memory state — the magic-link flow
 * hard-redirects to `/` — so the token is stashed in localStorage before auth
 * starts and resumed after any successful sign-in: `ClaimIntentResume` (mounted
 * in AppRoutes) watches auth and navigates back to /claim/<token>.
 */

const KEY = 'dunea:claim-intent';

/*
 * Shape guard, mirroring the server's (claims.controller.ts). Tokens are 16
 * random bytes base64url-encoded (22 chars); links minted before the shortening
 * are 64 hex chars and must keep resuming, so the range covers both. Keep this
 * in sync with the controller — a stricter regex here silently drops the intent
 * and the magic-link signup path loses the claim.
 */
const TOKEN_SHAPE = /^[A-Za-z0-9_-]{22,64}$/;

export function rememberClaimIntent(token: string): void {
  try {
    localStorage.setItem(KEY, token);
  } catch {
    // Private mode — the user just re-opens the link after signing in.
  }
}

export function pendingClaimIntent(): string | null {
  try {
    const token = localStorage.getItem(KEY);

    return token && TOKEN_SHAPE.test(token) ? token : null;
  } catch {
    return null;
  }
}

export function clearClaimIntent(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // Nothing to clear.
  }
}

/**
 * Clear the intent ONLY if it is the one for `token`. A recipient can hold more
 * than one claim link: opening a dead link B must not discard a still-pending
 * intent for link A, which an unconditional clear would do.
 */
export function clearClaimIntentFor(token: string): void {
  if (pendingClaimIntent() === token) {
    clearClaimIntent();
  }
}
